import React from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import Container from "@/components/ui/Container";
import { Check } from "lucide-react";
import { team } from "@/data/team";
import Vacancies from "@/components/sections/Vacancies";
import { usePageTitle } from "@/hooks/usePageTitle";

const AboutPage = () => {
  usePageTitle("О компании");

  const values = [
    {
      title: "Инновации",
      description: "Мы постоянно ищем новые идеи и технологии, чтобы предложить нашим клиентам самые современные и эффективные решения."
    },
    {
      title: "Качество",
      description: "Мы придерживаемся высоких стандартов качества во всем, что делаем, обеспечивая надежность и стабильность наших продуктов."
    },
    {
      title: "Персонализация",
      description: "Мы ставим потребности клиента в центр нашей работы, обеспечивая индивидуальный подход к каждому проекту."
    },
    {
      title: "Профессионализм",
      description: "Наша команда состоит из опытных специалистов, которые постоянно совершенствуют свои навыки и знания."
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 bg-secondary">
          <Container>
            <div className="max-w-3xl mx-auto text-center">
              <div className="tag mb-6 animate-fade-in">О компании</div>
              <h1 className="heading text-4xl md:text-5xl font-bold mb-6 animate-fade-in" style={{ animationDelay: "0.2s" }}>
                Мы создаем <span className="text-gradient">технологии будущего</span> для вашего бизнеса
              </h1>
              <p className="text-muted-foreground text-lg mb-0 animate-fade-in" style={{ animationDelay: "0.4s" }}>
                StackAdvance - ваш надежный партнер в разработке инновационного программного обеспечения для ритейла, e-commerce и логистики.
              </p>
            </div>
          </Container>
        </section>

        {/* About Section */}
        <section className="py-20">
          <Container>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              <div>
                <div className="tag mb-4">Наша история</div>
                <h2 className="heading text-3xl font-bold mb-6">
                  Как мы начинали и к чему стремимся
                </h2>
                <p className="text-muted-foreground mb-6">
                  Компания StackAdvance была основана в 2015 году группой энтузиастов, объединенных идеей создания интеллектуальных программных решений для бизнеса. Мы начинали как небольшая команда из пяти разработчиков, работающих над одним проектом для ритейл-сети.
                </p>
                <p className="text-muted-foreground mb-6">
                  Сегодня StackAdvance - это команда из более чем 50 высококвалифицированных специалистов, реализовавших десятки успешных проектов для клиентов из разных отраслей. Мы постоянно растем и развиваемся, осваивая новые технологии и подходы к разработке программного обеспечения.
                </p>
                <p className="text-muted-foreground">
                  Наша миссия - помогать бизнесу использовать все возможности современных технологий для роста, оптимизации процессов и улучшения клиентского опыта. Мы стремимся быть не просто подрядчиком, а надежным технологическим партнером для наших клиентов.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-border">
                  <div className="w-12 h-12 bg-primary/10 flex items-center justify-center rounded-lg mb-5 text-primary">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 22h14"></path>
                      <path d="M5 2h14"></path>
                      <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"></path>
                      <path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">7+ лет</h3>
                  <p className="text-muted-foreground text-sm">На рынке разработки программного обеспечения</p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-border">
                  <div className="w-12 h-12 bg-primary/10 flex items-center justify-center rounded-lg mb-5 text-primary">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">50+ экспертов</h3>
                  <p className="text-muted-foreground text-sm">В команде разработки и поддержки</p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-border">
                  <div className="w-12 h-12 bg-primary/10 flex items-center justify-center rounded-lg mb-5 text-primary">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m12 14 4-4"></path>
                      <path d="M3.34 19a10 10 0 1 1 17.32 0"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">100+ проектов</h3>
                  <p className="text-muted-foreground text-sm">Успешно реализованных для клиентов</p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-border">
                  <div className="w-12 h-12 bg-primary/10 flex items-center justify-center rounded-lg mb-5 text-primary">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14"></path>
                      <path d="M16.5 9.4 7.55 4.24"></path>
                      <polyline points="3.29 7 12 12 20.71 7"></polyline>
                      <line x1="12" x2="12" y1="22" y2="12"></line>
                      <circle cx="18.5" cy="15.5" r="2.5"></circle>
                      <path d="M20.27 17.27 22 19"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">30+ клиентов</h3>
                  <p className="text-muted-foreground text-sm">Доверяют нам свой бизнес</p>
                </div>
              </div>
            </div>
          </Container>
        </section>

        {/* Values Section */}
        <section className="py-20 bg-secondary">
          <Container>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <div className="tag mb-4 mx-auto inline-block">Наши ценности</div>
              <h2 className="heading text-3xl font-bold mb-6">
                Принципы, которыми мы руководствуемся
              </h2>
              <p className="text-muted-foreground">
                Наши ценности определяют все, что мы делаем — от процесса разработки до взаимодействия с клиентами. Они помогают нам создавать качественные продукты и выстраивать долгосрочные отношения.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {values.map((value, index) => (
                <div key={index} className="glass rounded-2xl p-6">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-primary/10 flex items-center justify-center rounded-lg mr-4 text-primary shrink-0">
                      <Check className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">{value.title}</h3>
                      <p className="text-muted-foreground">{value.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* Team Section */}
        {/* <section className="py-20">
          <Container>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <div className="tag mb-4 mx-auto inline-block">Наша команда</div>
              <h2 className="heading text-3xl font-bold mb-6">
                Познакомьтесь с экспертами StackAdvance
              </h2>
              <p className="text-muted-foreground">
                Наша команда состоит из опытных профессионалов, объединенных общей миссией — создавать инновационные технологические решения, которые помогают бизнесу развиваться и достигать новых высот.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {team.map((member, index) => (
                <div key={index} className="group relative">
                  <div className="aspect-[3/4] overflow-hidden rounded-2xl bg-secondary mb-4">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="h-full w-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <h3 className="text-lg font-semibold">{member.name}</h3>
                  <p className="text-muted-foreground">{member.position}</p>
                </div>
              ))}
            </div>
          </Container>
        
        </section> */}
        {/* <Vacancies /> */}
      </main>
      <Footer />
    </div>
  );
};

export default AboutPage;
